import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    ChevronDown, 
    Medal, 
    Coins, 
    Swords, 
    Box, 
    Package,
    TrendingUp,
    Gavel,
    ArrowLeftRight
} from 'lucide-react';

// Icon mapping for categories
export const categoryIcons = {
    'cat_faleristica': Medal,
    'cat_numismatica': Coins,
    'cat_militare': Swords,
    'cat_diverse': Box,
    'faleristica': Medal,
    'numismatica': Coins,
    'efecte-militare': Swords,
    'diverse': Box,
    'Medal': Medal,
    'Coins': Coins,
    'Shield': Swords,
    'Package': Box,
};

// Default/fallback categories (used while loading)
export const defaultCategories = [
    {
        category_id: 'cat_faleristica',
        name: 'Faleristică',
        slug: 'faleristica',
        icon: 'Medal',
        subcategories: [
            { subcategory_id: 'sub_ordine', name: 'Ordine', slug: 'ordine' },
            { subcategory_id: 'sub_medalii_f', name: 'Medalii', slug: 'medalii' },
            { subcategory_id: 'sub_titluri', name: 'Titluri', slug: 'titluri' },
            { subcategory_id: 'sub_insigne', name: 'Insigne', slug: 'insigne' },
            { subcategory_id: 'sub_brevete', name: 'Brevete', slug: 'brevete' },
            { subcategory_id: 'sub_diplome', name: 'Diplome', slug: 'diplome' },
            { subcategory_id: 'sub_loturi_f', name: 'Loturi', slug: 'loturi' },
            { subcategory_id: 'sub_diverse_f', name: 'Diverse', slug: 'diverse' }
        ]
    },
    {
        category_id: 'cat_numismatica',
        name: 'Numismatică',
        slug: 'numismatica',
        icon: 'Coins',
        subcategories: [
            { subcategory_id: 'sub_monede', name: 'Monede', slug: 'monede' },
            { subcategory_id: 'sub_bancnote', name: 'Bancnote', slug: 'bancnote' },
            { subcategory_id: 'sub_medalii_n', name: 'Medalii', slug: 'medalii' },
            { subcategory_id: 'sub_jetoane', name: 'Jetoane', slug: 'jetoane' },
            { subcategory_id: 'sub_token', name: 'Token', slug: 'token' },
            { subcategory_id: 'sub_loturi_n', name: 'Loturi', slug: 'loturi' }
        ]
    },
    {
        category_id: 'cat_militare',
        name: 'Militare',
        slug: 'efecte-militare',
        icon: 'Shield',
        subcategories: [
            { subcategory_id: 'sub_uniforme', name: 'Uniforme', slug: 'uniforme' },
            { subcategory_id: 'sub_epoleti', name: 'Epoleți', slug: 'epoleti' },
            { subcategory_id: 'sub_arme', name: 'Arme', slug: 'arme' },
            { subcategory_id: 'sub_loturi_m', name: 'Loturi', slug: 'loturi' },
            { subcategory_id: 'sub_diverse_m', name: 'Diverse', slug: 'diverse' }
        ]
    },
    {
        category_id: 'cat_diverse',
        name: 'Diverse',
        slug: 'diverse',
        icon: 'Package',
        subcategories: [
            { subcategory_id: 'sub_pionieri', name: 'Pionieri', slug: 'pionieri' },
            { subcategory_id: 'sub_poze', name: 'Poze', slug: 'poze' },
            { subcategory_id: 'sub_carti', name: 'Cărți', slug: 'carti' },
            { subcategory_id: 'sub_tablouri', name: 'Tablouri', slug: 'tablouri' },
            { subcategory_id: 'sub_bijuterii', name: 'Bijuterii', slug: 'bijuterii' },
            { subcategory_id: 'sub_loturi_d', name: 'Loturi', slug: 'loturi' }
        ]
    }
];

// Desktop hover dropdown for categories
export const HoverDropdown = ({ category }) => {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef(null);
    const navigate = useNavigate();
    
    const IconComponent = categoryIcons[category.icon] || categoryIcons[category.category_id] || categoryIcons[category.slug] || Box;

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
    };
    
    const subcategories = category.subcategories || [];

    return (
        <div 
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button 
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    isOpen ? 'bg-gray-200' : 'text-foreground hover:bg-gray-200'
                }`}
                onClick={() => navigate(`/category/${category.slug}`)}
            >
                <IconComponent className="w-4 h-4" />
                {category.name}
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-[220px] z-50 animate-in fade-in-0 zoom-in-95 duration-100 rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white/95">
                    {/* Content */}
                    <div className="py-1">
                        <Link 
                            to={`/category/${category.slug}`}
                            className="block px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1"
                            onClick={() => setIsOpen(false)}
                        >
                            Toate în {category.name}
                        </Link>
                        {subcategories.length > 0 && <div className="h-px bg-gray-200/50 my-1" />}
                        {subcategories.map((sub) => {
                            const subSlug = typeof sub === 'string' ? sub.toLowerCase() : sub.slug;
                            const subName = typeof sub === 'string' ? sub : sub.name;
                            const subKey = typeof sub === 'string' ? sub : sub.subcategory_id;
                            
                            return (
                                <Link
                                    key={subKey}
                                    to={`/category/${category.slug}/${subSlug}`}
                                    className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {subName}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// "Toate" hover dropdown
export const ToateHoverDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef(null);
    const navigate = useNavigate();

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
    };

    return (
        <div 
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button 
                onClick={() => navigate('/all-listings')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    isOpen ? 'bg-gray-200' : 'text-foreground hover:bg-gray-200'
                }`}
            >
                <Package className="w-4 h-4" />
                Toate
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-[200px] z-50 animate-in fade-in-0 zoom-in-95 duration-100 rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white/95">
                    {/* Content */}
                    <div className="py-1">
                        <button 
                            onClick={() => { navigate('/all-listings'); setIsOpen(false); }}
                            className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]"
                        >
                            <Package className="w-4 h-4 mr-2" />
                            Toate anunțurile
                        </button>
                        <button 
                            onClick={() => { navigate('/all-sales'); setIsOpen(false); }}
                            className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]"
                        >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Doar preț fix
                        </button>
                        <button 
                            onClick={() => { navigate('/all-auctions'); setIsOpen(false); }}
                            className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]"
                        >
                            <Gavel className="w-4 h-4 mr-2" />
                            Doar licitații
                        </button>
                        <button 
                            onClick={() => { navigate('/trades'); setIsOpen(false); }}
                            className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]"
                        >
                            <ArrowLeftRight className="w-4 h-4 mr-2" />
                            Schimburi
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Mobile category item with expandable subcategories
export const MobileCategoryItem = ({ category, onClose }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const IconComponent = categoryIcons[category.icon] || categoryIcons[category.category_id] || Box;
    const subcategories = category.subcategories || [];

    return (
        <div className="border-b border-gray-100 last:border-b-0">
            <div className="flex items-center justify-between">
                <Link
                    to={`/category/${category.slug}`}
                    className="flex-1 flex items-center gap-3 px-4 py-3 text-foreground hover:bg-gray-200 transition-colors"
                    onClick={onClose}
                >
                    <IconComponent className="w-5 h-5 text-primary" />
                    <span className="font-medium">{category.name}</span>
                </Link>
                {subcategories.length > 0 && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-3 hover:bg-gray-200 transition-colors"
                    >
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                )}
            </div>
            
            {isExpanded && subcategories.length > 0 && (
                <div className="bg-gray-50 py-1">
                    {subcategories.map((sub) => {
                        const subSlug = typeof sub === 'string' ? sub.toLowerCase() : sub.slug;
                        const subName = typeof sub === 'string' ? sub : sub.name;
                        const subKey = typeof sub === 'string' ? sub : sub.subcategory_id;
                        
                        return (
                            <Link
                                key={subKey}
                                to={`/category/${category.slug}/${subSlug}`}
                                className="block px-4 py-2.5 pl-12 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors"
                                onClick={onClose}
                            >
                                {subName}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Mobile Categories Panel
export const MobileCategoriesPanel = ({ categories, isOpen, onClose, navigate }) => {
    if (!isOpen) return null;
    
    return (
        <div className="lg:hidden border-t bg-white/95 animate-slide-down max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
                {/* Filter menu items */}
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">FILTRE</h3>
                
                <nav className="space-y-1 mb-4">
                    <div className="border-b border-gray-100">
                        <button
                            onClick={() => { navigate('/all-sales'); onClose(); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-foreground hover:bg-gray-200 transition-colors"
                            data-testid="mobile-sales-link"
                        >
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            <span className="font-medium">Preț fix</span>
                        </button>
                    </div>
                    <div className="border-b border-gray-100">
                        <button
                            onClick={() => { navigate('/all-auctions'); onClose(); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-foreground hover:bg-gray-200 transition-colors"
                            data-testid="mobile-auctions-link"
                        >
                            <Gavel className="w-5 h-5 text-blue-500" />
                            <span className="font-medium">Licitații</span>
                        </button>
                    </div>
                    <div className="border-b border-gray-100 last:border-b-0">
                        <button
                            onClick={() => { navigate('/trades'); onClose(); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-foreground hover:bg-gray-200 transition-colors"
                            data-testid="mobile-trades-link"
                        >
                            <ArrowLeftRight className="w-5 h-5 text-orange-500" />
                            <span className="font-medium">Schimburi</span>
                        </button>
                    </div>
                </nav>
                
                {/* Separator */}
                <div className="border-t border-gray-200 my-4"></div>
                
                {/* Categories */}
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">CATEGORII</h3>
                
                <nav className="space-y-1">
                    {categories.map((category) => (
                        <MobileCategoryItem 
                            key={category.category_id || category.id} 
                            category={category} 
                            onClose={onClose} 
                        />
                    ))}
                </nav>
            </div>
        </div>
    );
};
